import clsx from 'clsx';
import type * as React from 'react';

import type { NotificationMetadata } from '@gitbook/app-notifications';
import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';
import { Badge } from '@gitbook/spine-react';

import { UserAvatar } from '../Users';

/**
 * Render an appropriate notification for an icon. If the notification has
 * an actor associated with it, render a stacked icon. Otherwise, render a
 * simple icon.
 */
export function NotificationIcon(props: {
    metadata: NotificationMetadata | null;
    className?: string;
}): React.ReactElement {
    const { metadata } = props;
    const actor = getActorUserId(metadata);

    return actor ? (
        <StackedNotificationIcon {...props} actor={actor} />
    ) : (
        <ActionIconBadge {...props} className="mr-3" />
    );
}

/**
 * A notification icon with both event and actor icons.
 */
function StackedNotificationIcon(props: {
    metadata: NotificationMetadata | null;
    actor: string;
    className?: string;
}): React.ReactElement {
    const { metadata, actor, className } = props;

    return (
        <div className={clsx('relative mr-4', className)}>
            <UserAvatar size={28} user={actor} />
            <div className="absolute -right-1.5 -bottom-1.5 z-10 rounded-full border-[1.5px] border-(--neutral-2) bg-(--neutral-3) group-hover/notification-item:border-(--neutral-3)">
                <Badge
                    icon={getActionIcon(metadata)}
                    iconOnly
                    kind="secondary"
                    size="xxsmall"
                    className={className}
                />
            </div>
        </div>
    );
}

/**
 * An icon showing the action of the notification.
 */
function ActionIconBadge(props: {
    metadata: NotificationMetadata | null;
    className?: string;
}): React.ReactElement {
    const { metadata, className } = props;

    return (
        <Badge
            icon={getActionIcon(metadata)}
            iconOnly
            kind="secondary"
            size="medium"
            className={className}
            blend
        />
    );
}

function getActionIcon(metadata: NotificationMetadata | null): SVGIconComponent {
    switch (metadata?.type) {
        case 'change_request_review_requested':
            return Icon.ChangeRequestReviewRequest;
        case 'comment_mention':
            return Icon.AtSign;
        case 'comment_posted':
        case 'comment_reply':
            return Icon.Comment;
        case 'upgrade_requested':
            return Icon.Upgrade;
        default:
            return Icon.ExclamationCircle;
    }
}

/**
 * An icon for the actor of a notification. Fallsback to a generic user icon if needed.
 */
function getActorUserId(metadata: NotificationMetadata | null): string | undefined {
    switch (metadata?.type) {
        case 'comment_mention':
        case 'comment_posted':
        case 'comment_reply':
            return metadata.postedBy;

        case 'change_request_review_requested':
            return metadata.requestedBy;
    }
}
